/** The Partner Central Selling API's Customer.Account.Address.CountryCode
 *  enum (from the installed SDK model) — the ONLY values ACE accepts. Names
 *  render via Intl.DisplayNames so we don't maintain a 249-entry map. */
export const ACE_COUNTRY_CODES = 'US,AF,AX,AL,DZ,AS,AD,AO,AI,AQ,AG,AR,AM,AW,AU,AT,AZ,BS,BH,BD,BB,BY,BE,BZ,BJ,BM,BT,BO,BQ,BA,BW,BV,BR,IO,BN,BG,BF,BI,KH,CM,CA,CV,KY,CF,TD,CL,CN,CX,CC,CO,KM,CG,CK,CR,CI,HR,CU,CW,CY,CZ,CD,DK,DJ,DM,DO,EC,EG,SV,GQ,ER,EE,ET,FK,FO,FJ,FI,FR,GF,PF,TF,GA,GM,GE,DE,GH,GI,GR,GL,GD,GP,GU,GT,GG,GN,GW,GY,HT,HM,VA,HN,HK,HU,IS,IN,ID,IR,IQ,IE,IM,IL,IT,JM,JP,JE,JO,KZ,KE,KI,KR,KW,KG,LA,LV,LB,LS,LR,LY,LI,LT,LU,MO,MK,MG,MW,MY,MV,ML,MT,MH,MQ,MR,MU,YT,MX,FM,MD,MC,MN,ME,MS,MA,MZ,MM,NA,NR,NP,NL,AN,NC,NZ,NI,NE,NG,NU,NF,MP,NO,OM,PK,PW,PS,PA,PG,PY,PE,PH,PN,PL,PT,PR,QA,RE,RO,RU,RW,BL,SH,KN,LC,MF,PM,VC,WS,SM,ST,SA,SN,RS,SC,SL,SG,SX,SK,SI,SB,SO,ZA,GS,SS,ES,LK,SD,SR,SJ,SZ,SE,CH,SY,TW,TJ,TZ,TH,TL,TG,TK,TO,TT,TN,TR,TM,TC,TV,UG,UA,AE,GB,UM,UY,UZ,VU,VE,VN,VG,VI,WF,EH,YE,ZM,ZW'.split(',')

const display = new Intl.DisplayNames(['en'], { type: 'region' })

function nameOf(code: string): string {
  try {
    return display.of(code) ?? code
  } catch {
    return code
  }
}

/** [code, name] pairs — US first (the overwhelmingly common case), then the
 *  rest alphabetical by display name. */
export const ACE_COUNTRIES: [string, string][] = [
  ['US', nameOf('US')],
  ...ACE_COUNTRY_CODES.filter((c) => c !== 'US')
    .map((c): [string, string] => [c, nameOf(c)])
    .sort((a, b) => a[1].localeCompare(b[1])),
]

// A few names Intl won't produce / people commonly type, for HQ matching.
const NAME_ALIASES: Record<string, string> = {
  uk: 'GB', 'united kingdom': 'GB', england: 'GB', 'great britain': 'GB',
  usa: 'US', 'united states': 'US', 'united states of america': 'US', america: 'US',
  uae: 'AE', 'south korea': 'KR', korea: 'KR', russia: 'RU', vietnam: 'VN',
  'czech republic': 'CZ', holland: 'NL', 'the netherlands': 'NL',
}

let nameToCode: Map<string, string> | null = null

/** Match a free-typed country name (from an HQ string like "Berlin, Germany")
 *  to an ACE country code, or null when it isn't a country. */
export function countryCodeFromName(name: string | undefined | null): string | null {
  const key = (name ?? '').trim().toLowerCase()
  if (!key) return null
  if (NAME_ALIASES[key]) return NAME_ALIASES[key]
  if (!nameToCode) {
    nameToCode = new Map(ACE_COUNTRIES.map(([code, n]) => [n.toLowerCase(), code]))
  }
  return nameToCode.get(key) ?? null
}
